import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Database, Settings, CheckCircle2, XCircle, RefreshCw, ExternalLink,
  ArrowUpRight, ArrowDownRight, Eye, MousePointerClick, DollarSign, Users, Activity,
  Copy, Clock, Trash2, TrendingUp, BarChart3, Search, X, Download, Brain, Target,
  ChevronRight, Sparkles, Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line
} from "recharts";

const WENDY_IMG = "/images/wendy-5x-pipeline.jpg";
const CHART_COLORS = ["#00A5A8", "#C749D3", "#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#64748B", "#8B5CF6"];
const SOURCE_COLORS = { hubspot: "#F97316", adroll: "#3B82F6", commonroom: "#A855F7" };

export default function UnifiedReportingPage() {
  const { sessionToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetail, setAccountDetail] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);

  // Config state
  const [adrollApiKey, setAdrollApiKey] = useState("");
  const [adrollEid, setAdrollEid] = useState("");
  const [crWebhookSecret, setCrWebhookSecret] = useState("");
  const [hubspotListId, setHubspotListId] = useState("");
  const [savingAdroll, setSavingAdroll] = useState(false);
  const [savingCr, setSavingCr] = useState(false);
  const [syncingAdroll, setSyncingAdroll] = useState(false);
  const [syncingHubspot, setSyncingHubspot] = useState(false);
  const [purging, setPurging] = useState(false);

  const tenantId = sessionStorage.getItem("viewing_tenant_id");
  const headers = { Authorization: `Bearer ${sessionToken}` };

  const fetchStatus = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await axios.get(`${API}/unified-reporting/webhooks/status?tenant_id=${tenantId}`, { headers });
      setWebhookStatus(res.data);
    } catch (e) { console.error("Status error:", e); }
  }, [tenantId]);

  const fetchAccounts = useCallback(async () => {
    if (!tenantId) return;
    setAccountsLoading(true);
    try {
      const res = await axios.get(`${API}/unified-reporting/accounts?tenant_id=${tenantId}&limit=200`, { headers });
      setAccounts(res.data.accounts || []);
    } catch (e) {
      console.error("Accounts error:", e);
    }
    setAccountsLoading(false);
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      setLoading(true);
      Promise.all([fetchStatus(), fetchAccounts()]).finally(() => setLoading(false));
    }
  }, [tenantId]);

  const fetchAccountDetail = async (domain) => {
    try {
      const res = await axios.get(`${API}/unified-reporting/accounts/${domain}?tenant_id=${tenantId}`, { headers });
      setAccountDetail(res.data);
      setSelectedAccount(domain);
    } catch (e) { toast.error("Failed to load account detail"); }
  };

  const saveAdroll = async () => {
    if (!adrollApiKey || !adrollEid) { toast.error("Both fields required"); return; }
    setSavingAdroll(true);
    try {
      await axios.post(`${API}/unified-reporting/adroll/configure?tenant_id=${tenantId}&api_key=${encodeURIComponent(adrollApiKey)}&advertisable_eid=${encodeURIComponent(adrollEid)}`, {}, { headers });
      toast.success("AdRoll credentials saved");
      setAdrollApiKey(""); setAdrollEid("");
      fetchStatus();
    } catch (e) { toast.error("Failed to save"); }
    setSavingAdroll(false);
  };

  const saveCrSecret = async () => {
    if (!crWebhookSecret) { toast.error("Secret required"); return; }
    setSavingCr(true);
    try {
      await axios.post(`${API}/unified-reporting/webhooks/configure?tenant_id=${tenantId}&integration=commonroom&secret=${encodeURIComponent(crWebhookSecret)}`, {}, { headers });
      toast.success("Webhook secret saved");
      setCrWebhookSecret("");
      fetchStatus();
    } catch (e) { toast.error("Failed to save"); }
    setSavingCr(false);
  };

  const syncAdroll = async () => {
    setSyncingAdroll(true);
    const end = new Date().toISOString().split("T")[0];
    const start = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
    try {
      const res = await axios.post(`${API}/unified-reporting/adroll/sync?tenant_id=${tenantId}&start_date=${start}&end_date=${end}`, {}, { headers });
      toast.success(`AdRoll synced: ${res.data.accounts_stored} accounts`);
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || "AdRoll sync failed"); }
    setSyncingAdroll(false);
  };

  const syncHubspot = async () => {
    if (!hubspotListId) { toast.error("Enter a HubSpot list ID"); return; }
    setSyncingHubspot(true);
    try {
      const res = await axios.post(`${API}/unified-reporting/hubspot/sync-segment?tenant_id=${tenantId}&list_id=${hubspotListId}`, {}, { headers });
      toast.success(`HubSpot synced: ${res.data.accounts_stored} accounts`);
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || "HubSpot sync failed"); }
    setSyncingHubspot(false);
  };

  const purgeMockData = async () => {
    if (!window.confirm("This will permanently remove all mock/demo data. Real webhook and sync data will be preserved. Continue?")) return;
    setPurging(true);
    try {
      const res = await axios.delete(`${API}/unified-reporting/mock/purge?tenant_id=${tenantId}`, { headers });
      toast.success(`Purged: ${res.data.deleted.commonroom_signals} signals, ${res.data.deleted.adroll_metrics} AdRoll, ${res.data.deleted.hubspot_accounts} HubSpot`);
      fetchAccounts();
      fetchStatus();
    } catch (e) { toast.error("Purge failed"); }
    setPurging(false);
  };

  const webhookUrl = `${window.location.origin}/api/unified-reporting/webhooks/commonroom/${tenantId}`;

  const exportCSV = async (type) => {
    const url = type === "accounts"
      ? `${API}/linkedin-abm-agent/export/accounts-csv?tenant_id=${tenantId}`
      : `${API}/linkedin-abm-agent/export/dashboard-csv?tenant_id=${tenantId}`;
    try {
      const res = await axios.get(url, { headers, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = type === "accounts" ? "unified_accounts.csv" : "dashboard_summary.csv";
      link.click();
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  // Derived chart data
  const filteredAccounts = accounts.filter(a =>
    !searchQuery || a.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) || a.domain?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const topAccountsByScore = [...accounts].sort((a, b) => b.total_score - a.total_score).slice(0, 10);
  const topByImpressions = [...accounts].sort((a, b) => b.ar_impressions - a.ar_impressions).slice(0, 8);
  const topBySignals = [...accounts].sort((a, b) => b.cr_total_signals - a.cr_total_signals).slice(0, 8);

  const sourceDistribution = [
    { name: "HubSpot only", value: accounts.filter(a => a.sources?.hubspot && !a.sources?.adroll && !a.sources?.commonroom).length },
    { name: "AdRoll only", value: accounts.filter(a => !a.sources?.hubspot && a.sources?.adroll && !a.sources?.commonroom).length },
    { name: "CommonRoom only", value: accounts.filter(a => !a.sources?.hubspot && !a.sources?.adroll && a.sources?.commonroom).length },
    { name: "2 sources", value: accounts.filter(a => [a.sources?.hubspot, a.sources?.adroll, a.sources?.commonroom].filter(Boolean).length === 2).length },
    { name: "All 3 sources", value: accounts.filter(a => a.sources?.hubspot && a.sources?.adroll && a.sources?.commonroom).length },
  ].filter(d => d.value > 0);

  const totalImpressions = accounts.reduce((s, a) => s + (a.ar_impressions || 0), 0);
  const totalClicks = accounts.reduce((s, a) => s + (a.ar_clicks || 0), 0);
  const totalSpend = accounts.reduce((s, a) => s + (a.ar_cost || 0), 0);
  const totalSignals = accounts.reduce((s, a) => s + (a.cr_total_signals || 0), 0);
  const totalContacts = accounts.reduce((s, a) => s + (a.hs_contact_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="unified-reporting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Unified reporting agent</h1>
          <p className="text-sm text-slate-500 mt-1">Account-level intelligence across HubSpot, AdRoll, and CommonRoom</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => exportCSV("dashboard")} size="sm" variant="outline" data-testid="export-dashboard-csv"><Download className="w-4 h-4 mr-1" />Summary CSV</Button>
          <Button onClick={() => exportCSV("accounts")} size="sm" variant="outline" data-testid="export-accounts-csv"><Download className="w-4 h-4 mr-1" />Accounts CSV</Button>
          {accounts.length > 0 && <Badge variant="secondary" className="text-xs">{accounts.length} accounts</Badge>}
        </div>
      </div>

      {/* Date range picker */}
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2.5 border" data-testid="date-range-picker">
        <span className="text-xs font-medium text-slate-500">Date range</span>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36 h-8 text-xs" data-testid="ur-start-date" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36 h-8 text-xs" data-testid="ur-end-date" />
        <Button onClick={fetchAccounts} size="sm" variant="outline" className="h-8"><RefreshCw className="w-3 h-3 mr-1" />Apply</Button>
      </div>

      {/* Pipeline module teaser */}
      <Card className="border-slate-200 overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-slate-500">Upcoming module: Marketing pipeline contribution</CardTitle>
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 px-3 py-1">
              <Clock className="w-3 h-3 mr-1.5" />Coming end of July 2026
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="relative">
            <img src={WENDY_IMG} alt="Marketing pipeline contribution target" className="w-full object-contain max-h-[300px] bg-white" data-testid="wendy-pipeline-image" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <p className="text-white text-sm font-medium">This module will generate unified marketing pipeline contribution reports from all connected data sources</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LinkedIn ABM Agent preview */}
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50/50 to-white cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/app/linkedin-abm-agent")} data-testid="abm-agent-preview">
        <CardContent className="py-4 px-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="font-medium text-slate-800 flex items-center gap-2">LinkedIn ABM agent <Sparkles className="w-3.5 h-3.5 text-purple-500" /></div>
              <div className="text-xs text-slate-500">AI-powered campaign recommendations based on account activity trends</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts ({accounts.length})</TabsTrigger>
          <TabsTrigger value="configure" data-testid="tab-configure">Configure</TabsTrigger>
        </TabsList>

        {/* DASHBOARD TAB */}
        <TabsContent value="dashboard" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-slate-400 mr-2" /><span className="text-slate-500">Loading...</span></div>
          ) : accounts.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-12 text-center">
              <Database className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No data yet. Go to Configure tab to connect integrations or seed demo data.</p>
            </CardContent></Card>
          ) : (
            <>
              {/* KPI row */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> Accounts</div>
                  <div className="text-2xl font-bold mt-1">{accounts.length}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" /> Impressions</div>
                  <div className="text-2xl font-bold mt-1">{totalImpressions.toLocaleString()}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> Clicks</div>
                  <div className="text-2xl font-bold mt-1">{totalClicks.toLocaleString()}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Activity className="w-3 h-3" /> Signals</div>
                  <div className="text-2xl font-bold mt-1">{totalSignals.toLocaleString()}</div>
                </CardContent></Card>
                <Card><CardContent className="pt-4 pb-3 px-4">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> Spend</div>
                  <div className="text-2xl font-bold mt-1">${totalSpend.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                </CardContent></Card>
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top accounts by engagement score */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Top accounts by engagement score</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={topAccountsByScore} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="company_name" type="category" tick={{ fontSize: 10 }} width={120} />
                        <Tooltip formatter={(v) => [v.toLocaleString(), "Score"]} />
                        <Bar dataKey="total_score" fill="#00A5A8" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Source distribution pie */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Data source coverage</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={sourceDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, value }) => `${name}: ${value}`}>
                          {sourceDistribution.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Second charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* AdRoll impressions by account */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">AdRoll impressions by account</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={topByImpressions} margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="company_name" tick={{ fontSize: 9, angle: -30 }} height={60} interval={0} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                        <Tooltip formatter={(v) => [v.toLocaleString(), ""]} />
                        <Bar dataKey="ar_impressions" name="Impressions" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="ar_clicks" name="Clicks" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* CommonRoom signals by account */}
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">CommonRoom signals by account</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={topBySignals} margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="company_name" tick={{ fontSize: 9, angle: -30 }} height={60} interval={0} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="cr_total_signals" name="Total signals" fill="#A855F7" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="cr_unique_contacts" name="Unique contacts" fill="#D8B4FE" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* HubSpot contacts by account */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">HubSpot contacts in segment by account</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={[...accounts].sort((a,b) => b.hs_contact_count - a.hs_contact_count).slice(0, 12)} margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="company_name" tick={{ fontSize: 9, angle: -30 }} height={60} interval={0} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="hs_contact_count" name="Contacts" fill="#F97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ACCOUNTS TAB */}
        <TabsContent value="accounts" className="space-y-4">
          {accounts.length > 0 && (
            <>
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search accounts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-accounts-input"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>
                  )}
                </div>
                <Button onClick={fetchAccounts} size="sm" variant="outline"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
              </div>

              <Card>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="accounts-table">
                      <thead>
                        <tr className="border-b text-left text-xs text-slate-500 uppercase tracking-wider">
                          <th className="pb-3 pr-4">Account</th>
                          <th className="pb-3 pr-3 text-center">Score</th>
                          <th className="pb-3 pr-3 text-center"><span className="text-orange-500">HS</span> contacts</th>
                          <th className="pb-3 pr-3 text-center"><span className="text-blue-500">AR</span> impressions</th>
                          <th className="pb-3 pr-3 text-center"><span className="text-blue-500">AR</span> clicks</th>
                          <th className="pb-3 pr-3 text-center"><span className="text-blue-500">AR</span> spend</th>
                          <th className="pb-3 pr-3 text-center"><span className="text-purple-500">CR</span> signals</th>
                          <th className="pb-3 pr-3">Sources</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAccounts.map((acct) => (
                          <tr
                            key={acct.domain}
                            className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${selectedAccount === acct.domain ? 'bg-blue-50' : ''}`}
                            onClick={() => fetchAccountDetail(acct.domain)}
                            data-testid={`account-row-${acct.domain}`}
                          >
                            <td className="py-2.5 pr-4">
                              <div className="font-medium text-slate-800">{acct.company_name}</div>
                              <div className="text-xs text-slate-400">{acct.domain}</div>
                            </td>
                            <td className="py-2.5 pr-3 text-center"><Badge variant="secondary" className="font-mono text-xs">{Math.round(acct.total_score)}</Badge></td>
                            <td className="py-2.5 pr-3 text-center font-medium">{acct.hs_contact_count || "—"}</td>
                            <td className="py-2.5 pr-3 text-center">{acct.ar_impressions ? acct.ar_impressions.toLocaleString() : "—"}</td>
                            <td className="py-2.5 pr-3 text-center">{acct.ar_clicks || "—"}</td>
                            <td className="py-2.5 pr-3 text-center">{acct.ar_cost ? `$${acct.ar_cost.toFixed(0)}` : "—"}</td>
                            <td className="py-2.5 pr-3 text-center">{acct.cr_total_signals || "—"}</td>
                            <td className="py-2.5 pr-3">
                              <div className="flex gap-1.5">
                                {acct.sources?.hubspot && <span className="w-2.5 h-2.5 rounded-full bg-orange-400" title="HubSpot" />}
                                {acct.sources?.adroll && <span className="w-2.5 h-2.5 rounded-full bg-blue-400" title="AdRoll" />}
                                {acct.sources?.commonroom && <span className="w-2.5 h-2.5 rounded-full bg-purple-400" title="CommonRoom" />}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {accounts.length === 0 && !accountsLoading && (
            <Card className="border-dashed"><CardContent className="py-12 text-center">
              <Database className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No account data yet. Configure integrations to get started.</p>
            </CardContent></Card>
          )}

          {/* Account detail panel */}
          {selectedAccount && accountDetail && (
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white" data-testid="account-detail-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {accountDetail.company_name}
                    <span className="text-sm text-slate-400 font-normal ml-2">{accountDetail.domain}</span>
                  </CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => { setSelectedAccount(null); setAccountDetail(null); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* HubSpot */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-orange-600 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-orange-400" /> HubSpot
                    </h4>
                    {accountDetail.hubspot?.contacts?.length > 0 ? (
                      <div className="space-y-2">
                        {accountDetail.hubspot.contacts.map((c, i) => (
                          <div key={i} className="text-xs bg-white rounded-lg p-3 border shadow-sm">
                            <div className="font-medium text-slate-800">{c.name || c.email}</div>
                            <div className="text-slate-500 mt-0.5">{c.title}</div>
                            <Badge variant="outline" className="mt-1 text-[10px]">{c.lifecycle}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-slate-400">No HubSpot contacts</p>}
                  </div>

                  {/* AdRoll */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-400" /> AdRoll
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { label: "Impressions", value: (accountDetail.adroll?.total_impressions || 0).toLocaleString() },
                        { label: "Clicks", value: accountDetail.adroll?.total_clicks || 0 },
                        { label: "Spend", value: `$${(accountDetail.adroll?.total_cost || 0).toFixed(2)}` },
                        { label: "CTR", value: accountDetail.adroll?.total_impressions ? `${((accountDetail.adroll?.total_clicks / accountDetail.adroll?.total_impressions) * 100).toFixed(2)}%` : "—" },
                      ].map((m, i) => (
                        <div key={i} className="bg-white rounded-lg p-3 border shadow-sm text-center">
                          <div className="text-lg font-bold text-slate-800">{m.value}</div>
                          <div className="text-[10px] text-slate-400 uppercase tracking-wider">{m.label}</div>
                        </div>
                      ))}
                    </div>
                    {accountDetail.adroll?.metrics?.length > 1 && (
                      <div className="mt-2">
                        <ResponsiveContainer width="100%" height={120}>
                          <AreaChart data={[...accountDetail.adroll.metrics].reverse()}>
                            <Area type="monotone" dataKey="impressions" stroke="#3B82F6" fill="#DBEAFE" strokeWidth={2} />
                            <Tooltip formatter={(v) => [v.toLocaleString(), "Impressions"]} labelFormatter={(l) => `${accountDetail.adroll.metrics.find(m => m.impressions === l)?.start_date || ''}`} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* CommonRoom */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-purple-600 uppercase tracking-wider flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-purple-400" /> CommonRoom
                      <Badge variant="secondary" className="text-[10px] ml-auto">{accountDetail.commonroom?.total_signals || 0} signals</Badge>
                    </h4>
                    {accountDetail.commonroom?.signals?.length > 0 ? (
                      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                        {accountDetail.commonroom.signals.slice(0, 15).map((s, i) => (
                          <div key={i} className="text-xs bg-white rounded-lg p-2.5 border shadow-sm">
                            <div className="flex items-center justify-between">
                              <Badge variant="outline" className="text-[10px]">{s.event_type}</Badge>
                              <span className="text-slate-300 text-[10px]">{new Date(s.received_at).toLocaleDateString()}</span>
                            </div>
                            {s.contact_name && <div className="text-slate-700 mt-1 font-medium">{s.contact_name}</div>}
                            {s.contact_title && <div className="text-slate-400">{s.contact_title}</div>}
                            {s.activity_title && <div className="text-slate-500 mt-0.5">{s.activity_title}</div>}
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-slate-400">No CommonRoom signals</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* CONFIGURE TAB */}
        <TabsContent value="configure" className="space-y-4">
          {/* Status cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-purple-600 flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> CommonRoom</div>
                  {webhookStatus?.commonroom?.webhook_configured ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-400" />}
                </div>
                <div className="text-xl font-bold mt-2">{webhookStatus?.commonroom?.signals_total || 0} signals</div>
                <div className="text-[10px] text-slate-400">{webhookStatus?.commonroom?.signals_24h || 0} in last 24h</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-blue-600 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> AdRoll</div>
                  {webhookStatus?.adroll?.connected ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-400" />}
                </div>
                <div className="text-sm mt-2">{webhookStatus?.adroll?.connected ? "Connected" : "Not connected"}</div>
                <div className="text-[10px] text-slate-400">{webhookStatus?.adroll?.last_sync ? `Synced ${new Date(webhookStatus.adroll.last_sync).toLocaleDateString()}` : "No sync yet"}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-orange-600 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> HubSpot</div>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                </div>
                <div className="text-sm mt-2">OAuth connected</div>
                <div className="text-[10px] text-slate-400">Enter list ID below to sync segment</div>
              </CardContent>
            </Card>
          </div>

          {/* CommonRoom config */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Database className="w-4 h-4 text-purple-600" /> CommonRoom webhook</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Webhook URL — paste into CommonRoom Settings &rarr; Webhooks</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-slate-50 border rounded px-3 py-2 text-xs font-mono break-all" data-testid="webhook-url">{webhookUrl}</code>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }} data-testid="copy-webhook-url-btn"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Input type="password" placeholder="Webhook secret (optional)" value={crWebhookSecret} onChange={(e) => setCrWebhookSecret(e.target.value)} className="max-w-md" data-testid="cr-secret-input" />
                <Button onClick={saveCrSecret} disabled={savingCr} size="sm">{savingCr ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Save"}</Button>
              </div>
            </CardContent>
          </Card>

          {/* AdRoll config */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Eye className="w-4 h-4 text-blue-600" /> AdRoll API</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input type="password" placeholder="AdRoll API key" value={adrollApiKey} onChange={(e) => setAdrollApiKey(e.target.value)} data-testid="adroll-api-key-input" />
                <Input placeholder="Advertisable EID" value={adrollEid} onChange={(e) => setAdrollEid(e.target.value)} data-testid="adroll-eid-input" />
              </div>
              <Button onClick={saveAdroll} disabled={savingAdroll} size="sm">{savingAdroll ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : null}Save credentials</Button>
            </CardContent>
          </Card>

          {/* Sync controls */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Sync controls</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Input placeholder="HubSpot list/segment ID" value={hubspotListId} onChange={(e) => setHubspotListId(e.target.value)} className="max-w-xs" data-testid="hubspot-list-id-input" />
                <Button onClick={syncHubspot} disabled={syncingHubspot || !hubspotListId} size="sm">
                  {syncingHubspot ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Sync HubSpot
                </Button>
                <Button onClick={syncAdroll} disabled={syncingAdroll || !webhookStatus?.adroll?.connected} size="sm" variant="outline">
                  {syncingAdroll ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Sync AdRoll
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Purge mock data */}
          <Card className="border-red-200">
            <CardContent className="pt-4 pb-3 px-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-red-700">Purge demo data</div>
                <div className="text-xs text-slate-500">Remove all mock/demo data before going live. Real data from webhooks and syncs is preserved.</div>
              </div>
              <Button onClick={purgeMockData} disabled={purging} size="sm" variant="destructive" data-testid="purge-mock-btn">
                {purging ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}Purge mock data
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
